import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react'
import { ai_tutor_apis } from '../../../modules/apis'
import { useIAPProductIds, useUserTrialEligibility } from '../../../modules/apis/ai_tutor/queries'
import { IBetaAvailablePricingData } from '../../../modules/apis/ai_tutor/types'
import { axiosGet } from '../../../modules/axiosHelper'
import useTranslation from '../../../modules/i18n/useTranslation'
import {
  AITutorPlan,
  AOS_ANNUAL_ALL_IN_ONE_PLAN_ID,
  IAppPricingData,
  IOS_ANNUAL_ALL_IN_ONE_PLAN_ID,
  POST_MESSAGE_TYPES,
} from './constants'

interface AITutorPricingContextType {
  combinedPricings: AITutorPlan[]
  appPricingData: IAppPricingData[]
  setAppPricingData: Dispatch<SetStateAction<IAppPricingData[]>>
  eligibleForOffer: boolean
  selectedProduct: AITutorPlan
  setSelectedProduct: Dispatch<SetStateAction<AITutorPlan>>
  showIAPErrorPopup: boolean
  setShowIAPErrorPopup: Dispatch<SetStateAction<boolean>>
  iapErrorCode: string
  setIAPErrorCode: Dispatch<SetStateAction<string>>
}

const AITutorPricingContext = createContext<AITutorPricingContextType>({
  combinedPricings: [],
  appPricingData: null,
  setAppPricingData: () => {},
  eligibleForOffer: true,
  selectedProduct: null,
  setSelectedProduct: () => {},
  showIAPErrorPopup: false,
  setShowIAPErrorPopup: () => {},
  iapErrorCode: '',
  setIAPErrorCode: () => {},
})

export const useAITutorPricingContext = () => useContext(AITutorPricingContext)

const AITutorPricingProvider = ({ children }) => {
  const [appPricingData, setAppPricingData] = useState(null)
  const [betaPricingData, setBetaPricingData] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [platform, setPlatform] = useState(undefined)
  const [eligibleForOffer, setEligibleForOffer] = useState(null)
  const [showIAPErrorPopup, setShowIAPErrorPopup] = useState(false)
  const [iapErrorCode, setIAPErrorCode] = useState('')
  const { locale } = useTranslation()

  // Step 1: Get Product IDs & userTrialEligibility
  const { data: productIdsResponse } = useIAPProductIds()
  const { data: userEligibilityResponse } = useUserTrialEligibility(platform)

  useEffect(() => {
    if (userEligibilityResponse) {
      const eligibility = userEligibilityResponse.user_trial_eligibility
      setEligibleForOffer(eligibility)
    }
  }, [userEligibilityResponse])

  // Step 2: Pass On Product IDs to App
  useEffect(() => {
    if (productIdsResponse) {
      try {
        const productIds = productIdsResponse.product_ids
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: POST_MESSAGE_TYPES.IAP_PRODUCT_IDS,
            productIds: productIds,
          })
        )
      } catch (error) {
        console.log(error)
      }
    }
  }, [productIdsResponse])

  const getPlatformFromAppData = ({ appPricingData }: { appPricingData: any[] }): string => {
    if (appPricingData && appPricingData[0]) {
      const appPlatform = appPricingData[0].platform
      if (appPlatform === 'ios') {
        return 'apple_app_store'
      } else if (appPlatform === 'android') {
        return 'google_play_store'
      } else {
        alert('Failed to get platform info')
      }
    }
  }

  // Step 3: Get AppPricingData and Platform from App -> This is done in the webview file
  useEffect(() => {
    if (appPricingData) {
      setPlatform(getPlatformFromAppData({ appPricingData: appPricingData }))
      geAvailablePricing(getPlatformFromAppData({ appPricingData: appPricingData }), eligibleForOffer)
    }
  }, [appPricingData, eligibleForOffer])

  // Step 4: use Platform to get BetaPricingData
  const geAvailablePricing = async (platform, eligibility) => {
    const url = `${ai_tutor_apis.pricings.get()}?platform=${platform}&locale=${locale}&eligibility=${!!eligibility}`

    try {
      const res = await axiosGet(url)
      const pricings = res.pricings

      if (!Array.isArray(pricings)) {
        setBetaPricingData([pricings]) // Wrap it in an array if it's not an array
      } else {
        setBetaPricingData(pricings) // Use it directly if it's already an array
      }
    } catch (e) {
      // alert(e)
      console.error('Error fetching available pricing data:', e)
    }
  }

  const getFormattedPricings = ({
    betaPricingData,
    appPricingData,
  }: {
    betaPricingData: IBetaAvailablePricingData[]
    appPricingData: IAppPricingData[]
  }): AITutorPlan[] => {
    return appPricingData
      .map((appData) => {
        // Find the matching server data based on platform and productId
        const matchedBetaData = betaPricingData.find((betaData) => {
          if (appData.platform === 'ios' && betaData.platform === 'apple_app_store') {
            return betaData.apple_product_id === appData.productId
          } else if (appData.platform === 'android' && betaData.platform === 'google_play_store') {
            return (
              betaData.google_product_id === appData.productId &&
              betaData.google_base_plan_id === appData.googleBasePlanId &&
              betaData.google_offer_id === appData.googleOfferId
            )
          } else {
            return false
          }
        })

        if (!matchedBetaData) {
          // If no match is found, return null or handle accordingly
          return null
        }

        // Calculate periodPrice, monthlyPrice, and originalPrice
        const periodPrice = appData.periodPrice // Assuming periodPrice is a string and needs conversion
        const monthlyPrice =
          matchedBetaData.billing_period_unit === 'YEAR'
            ? parseFloat(appData.periodPrice) / (parseInt(matchedBetaData.billing_period_number) * 12)
            : parseFloat(appData.periodPrice) / parseInt(matchedBetaData.billing_period_number) // Assuming billing_period_number is in months
        const originalPrice = matchedBetaData.original_price
          ? parseFloat(matchedBetaData.original_price)
          : parseFloat(matchedBetaData.original_prices[appData.currency])

        const monthlyOriginalPrice =
          matchedBetaData.billing_period_unit === 'YEAR'
            ? originalPrice / (parseInt(matchedBetaData.billing_period_number) * 12)
            : originalPrice / parseInt(matchedBetaData.billing_period_number) // Assuming billing_period_number is in months

        if (!originalPrice) {
          return null
        }

        // Return formatted AITutorPlan
        return {
          aiTutorPlanPricingId: String(matchedBetaData.id),
          billingPeriodUnit: matchedBetaData.billing_period_unit,
          googleProductId: matchedBetaData.google_product_id,
          googleBasePlanId: matchedBetaData.google_base_plan_id, // Adjust logic if necessary
          googleOfferId: matchedBetaData.google_offer_id, // If there is no offer but a valid product, return null
          appleProductId: matchedBetaData.apple_product_id,
          name: matchedBetaData.name,
          periodPrice: periodPrice,
          monthlyPrice: monthlyPrice,
          originalPrice: originalPrice,
          monthlyOriginalPrice: monthlyOriginalPrice,
          isPopular:
            Number(matchedBetaData.id) === IOS_ANNUAL_ALL_IN_ONE_PLAN_ID ||
            Number(matchedBetaData.id) === AOS_ANNUAL_ALL_IN_ONE_PLAN_ID,
          offerType: appData.offerType,
          offerPeriodInDays: appData.offerPeriodInDays,
          offerPrice: appData.offerPrice,
          currency: appData.currency,
          platform: matchedBetaData.platform,
          permissions: matchedBetaData.permissions,
        }
      })
      .filter((plan) => plan !== null) // Filter out any unmatched entries (if no corresponding server data)
  }
  // Step 5: get Formatted Pricing using both appPricingData and betaPricingData
  const combinedPricings = useMemo(() => {
    if (betaPricingData && appPricingData) {
      try {
        const formattedPricings = getFormattedPricings({ betaPricingData, appPricingData })
        return formattedPricings
      } catch (error) {
        alert(`error ${error}`)
        console.log('An error occurred while formatting prices', error)
      }
    }
    return null // Return something else if needed when the condition is not met
  }, [betaPricingData, appPricingData])

  //  Step 6: set initially selected product which should be Premium Plus Annual
  useEffect(() => {
    if (combinedPricings && combinedPricings.length > 0) {
      let productFound = false

      // Try to find the preferred product first
      combinedPricings.forEach((formattedPricing, index) => {
        if (
          formattedPricing &&
          (Number(formattedPricing.aiTutorPlanPricingId) === IOS_ANNUAL_ALL_IN_ONE_PLAN_ID ||
            Number(formattedPricing.aiTutorPlanPricingId) === AOS_ANNUAL_ALL_IN_ONE_PLAN_ID)
        ) {
          setSelectedProduct(formattedPricing)
          productFound = true
        }
      })

      // Fallback: if preferred product not found, select the first available product
      if (!productFound && combinedPricings[0]) {
        setSelectedProduct(combinedPricings[0])
      }
    }
  }, [combinedPricings])

  return (
    <AITutorPricingContext.Provider
      value={{
        combinedPricings,
        appPricingData,
        setAppPricingData,
        eligibleForOffer,
        selectedProduct,
        setSelectedProduct,
        showIAPErrorPopup,
        setShowIAPErrorPopup,
        iapErrorCode,
        setIAPErrorCode,
      }}
    >
      {children}
    </AITutorPricingContext.Provider>
  )
}

export default AITutorPricingProvider
